import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Building2, AlertCircle, CheckCircle, Search } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface Practice {
  id: number;
  tin: string;
  tin_name: string;
  practice_alias: string;
  client_name: string;
  encounter_count: number;
}

interface PracticeSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (practiceId: number, ignoreTin?: boolean) => void;
  detectedTin?: string;
  tinMismatch?: boolean;
  userPractices: Practice[];
  isLoading?: boolean;
}

export function PracticeSelectionModal({
  open,
  onClose,
  onConfirm,
  detectedTin,
  tinMismatch = false,
  userPractices,
  isLoading = false,
}: PracticeSelectionModalProps) {
  const [selectedPracticeId, setSelectedPracticeId] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { toast } = useToast();

  // Filter practices based on search query
  const filteredPractices = useMemo(() => {
    if (!searchQuery.trim()) return userPractices;
    
    const query = searchQuery.toLowerCase().trim();
    return userPractices.filter(p => 
      p.tin_name?.toLowerCase().includes(query) ||
      p.practice_alias?.toLowerCase().includes(query) ||
      p.tin?.includes(query) ||
      p.client_name?.toLowerCase().includes(query)
    );
  }, [userPractices, searchQuery]);

  // Auto-select if TIN is detected
  useEffect(() => {
    if (detectedTin && userPractices.length > 0) {
      const matchedPractice = userPractices.find(p => p.tin === detectedTin);
      if (matchedPractice) {
        setSelectedPracticeId(matchedPractice.id.toString());
      }
    }
  }, [detectedTin, userPractices]);

  // Reset search when modal opens/closes
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
    }
  }, [open]);

  const handleConfirm = () => {
    if (!selectedPracticeId) {
      toast({
        title: "Practice Required",
        description: "Please select a practice before proceeding.",
        variant: "destructive",
      });
      return;
    }
    // Pass ignoreTin flag when there's a TIN mismatch
    onConfirm(parseInt(selectedPracticeId), tinMismatch);
  };

  const selectedPractice = userPractices.find(p => p.id.toString() === selectedPracticeId);

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {tinMismatch ? "TIN Not Found" : detectedTin ? "Confirm Practice Match" : "Select Practice"}
          </DialogTitle>
          <DialogDescription>
            {tinMismatch 
              ? `The file contains TIN ${detectedTin} which is not registered to any of your practices. Please select a practice for this upload.`
              : detectedTin 
              ? `TIN ${detectedTin} was detected in your file. Please confirm the practice.`
              : "Which practice does this data belong to?"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col py-2">
          {detectedTin && selectedPractice && !tinMismatch && (
            <Alert className="mb-3 flex-shrink-0">
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>TIN Match Found:</strong> {selectedPractice.tin_name || selectedPractice.practice_alias}
              </AlertDescription>
            </Alert>
          )}
          
          {tinMismatch && (
            <Alert className="mb-3 flex-shrink-0" variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Warning:</strong> The file's TIN will be ignored and data will be associated with the selected practice's TIN.
              </AlertDescription>
            </Alert>
          )}

          {/* Search bar - only show if more than 5 practices */}
          {userPractices.length > 5 && (
            <div className="mb-3 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, TIN, or client..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="text-xs text-muted-foreground mt-1.5 px-1">
                {searchQuery ? (
                  <>Showing {filteredPractices.length} of {userPractices.length} practices</>
                ) : (
                  <>{userPractices.length} practices available</>
                )}
              </div>
            </div>
          )}

          {/* Scrollable practice list */}
          <div className="flex-1 overflow-y-auto min-h-0 max-h-[300px] pr-1">
            <RadioGroup
              value={selectedPracticeId}
              onValueChange={setSelectedPracticeId}
              className="space-y-2"
            >
              {filteredPractices.length > 0 ? (
                filteredPractices.map((practice) => (
                  <div
                    key={practice.id}
                    className={`flex items-start space-x-3 rounded-lg border p-3 hover:bg-muted/50 transition-colors cursor-pointer ${
                      selectedPracticeId === practice.id.toString() ? 'border-primary bg-primary/5' : ''
                    }`}
                    onClick={() => setSelectedPracticeId(practice.id.toString())}
                  >
                    <RadioGroupItem value={practice.id.toString()} id={`practice-${practice.id}`} />
                    <Label
                      htmlFor={`practice-${practice.id}`}
                      className="flex-1 cursor-pointer space-y-1"
                    >
                      <div className="font-medium">
                        {practice.tin_name || practice.practice_alias}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        TIN: {practice.tin} • {practice.encounter_count.toLocaleString()} encounters in system
                      </div>
                      {practice.client_name && (
                        <div className="text-xs text-muted-foreground">
                          Client: {practice.client_name}
                        </div>
                      )}
                    </Label>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No practices match "{searchQuery}"</p>
                  <button 
                    onClick={() => setSearchQuery("")}
                    className="text-primary text-sm mt-1 hover:underline"
                  >
                    Clear search
                  </button>
                </div>
              )}
            </RadioGroup>
          </div>

          {!detectedTin && (
            <Alert className="mt-3 flex-shrink-0">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Tip:</strong> Including a TIN column in your file will auto-select the practice in future uploads.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-4">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading || !selectedPracticeId}>
            {detectedTin ? "Confirm & Upload" : "Start Processing"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
